import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Modal from '../ui/Modal';
import { SubscriptionService } from '../../api/generated/services/SubscriptionService';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';
import { addNotification } from '../../store/slices/notificationSlice';
import type { ProductView } from '../../api/generated/models/ProductView';
import type { SubscriptionChangePlanPreviewView } from '../../api/generated/models/SubscriptionChangePlanPreviewView';
import type { PendingInvoice } from './LicensePendingPaymentModal';

/**
 * Server response shape for `POST /v2/api/subscriptions/{id}/change-plan`.
 * The OpenAPI spec types the 202 response as a generic object, so we narrow
 * it locally based on the contract documented in the spec text.
 */
interface ChangePlanUpgradeResponse {
  direction: 'upgrade' | 'sidegrade';
  stripeInvoiceId?: string | null;
  hostedInvoiceUrl?: string | null;
  status?: 'paid' | 'open' | 'draft' | null;
  amountPaid?: number;
  /**
   * Present (true) only on the new synchronous sidegrade response
   * (`{ direction: 'sidegrade', applied: true, amountPaid: 0 }`). When set,
   * the change was applied server-side immediately - no Stripe invoice,
   * no 3DS, nothing to wait for.
   */
  applied?: boolean;
}

interface ChangePlanDowngradeResponse {
  direction: 'downgrade';
  effectiveAt: number;
  scheduledSubscriptionId?: string;
  stripeScheduleId?: string;
  message?: string;
}

type ChangePlanResponse = ChangePlanUpgradeResponse | ChangePlanDowngradeResponse;

export interface ChangePlanSuccessPayload {
  /**
   * Server-confirmed direction. Trust this over the preview's direction -
   * the prompt explicitly says: "if change-plan returned a different direction
   * than preview (e.g. a billing cycle webhook fired in between), trust
   * change-plan, not preview".
   */
  serverDirection: ChangePlanResponse['direction'];
  /** Unix timestamp from the downgrade response, if any. */
  effectiveAt?: number;
  /** Server reported "Scheduled change already exists". */
  alreadyScheduled?: boolean;
  /** For upgrade/sidegrade - Stripe invoice status. */
  status?: 'paid' | 'open' | 'draft' | null;
  /** Hosted invoice URL when Stripe needs SCA / 3DS / extra confirmation. */
  hostedInvoiceUrl?: string | null;
  /** Cents charged immediately, when applicable. */
  amountPaid?: number;
  newProduct: ProductView;
}

interface LicenseChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriptionId: string;
  /**
   * Display name of the currently active product (for the downgrade copy).
   * Sourced from `SubscriptionView.productName`, which is always present -
   * we deliberately don't require the full `ProductView` object, because the
   * active product may not be in the currently loaded product catalog.
   */
  currentProductName: string;
  /** Target product the user picked. */
  newProduct: ProductView;
  /**
   * Freshdesk add-on counts to send to BOTH preview and change-plan.
   * `undefined` = don't send the field = keep the subscription's current
   * value (per backend contract). Send a number to change it. Never send
   * these for non-Freshdesk plugins (backend returns 400).
   */
  emailExtraCount?: number;
  agentExtraCount?: number;
  /**
   * True if there is already a pending scheduled downgrade. Drives the
   * warning line "you already have a scheduled switch - confirming will
   * cancel it and schedule a new one". The backend handles the actual
   * cancellation transparently.
   */
  hasPendingScheduledChange: boolean;
  /** Pre-resolved name of the currently scheduled product (B), if any. */
  pendingPlanName?: string;
  /** Called after the API call succeeds, before the modal closes. */
  onSuccess: (payload: ChangePlanSuccessPayload) => void;
  /**
   * Notifies the parent whether a change-plan request is currently in flight,
   * so it can block the purchase/switch buttons on the cards (same UX as a
   * regular purchase). `false` is emitted only when the modal stays open
   * (validation / 503 retry); on success / 409 the parent owns the flag.
   */
  onSubmittingChange?: (submitting: boolean) => void;
  /**
   * Called when the backend rejects with 409 `pending_payment` - there is a
   * still-open Stripe invoice from a previous failed 3DS that must be paid
   * before any new plan change is allowed. The modal closes itself afterwards
   * so the parent can mount the pending-payment modal cleanly.
   */
  onPendingPayment: (invoice: PendingInvoice) => void;
}

type ModalState = 'preview_loading' | 'preview_error' | 'preview_ready' | 'submitting';

type PreviewDirection = SubscriptionChangePlanPreviewView['direction'];

const formatUnix = (unix: number | undefined): string => {
  if (typeof unix !== 'number' || !Number.isFinite(unix)) return '-';
  return new Date(unix * 1000).toLocaleDateString();
};

/**
 * Type guard for the 409 `pending_payment` error body. We only trust
 * `errorCode` (the prompt explicitly says don't pattern-match the error
 * string), and we defensively fall back to `null` if the inline schema
 * isn't shaped as expected.
 */
const extractPendingInvoice = (body: unknown): PendingInvoice | null => {
  if (!body || typeof body !== 'object') return null;
  const obj = body as Record<string, unknown>;
  if (obj.errorCode !== 'pending_payment') return null;
  const inv = obj.pendingInvoice;
  if (!inv || typeof inv !== 'object') return null;
  const i = inv as Record<string, unknown>;
  if (typeof i.id !== 'string') return null;
  if (typeof i.amountRemaining !== 'number') return null;
  return {
    id: i.id,
    hostedInvoiceUrl: typeof i.hostedInvoiceUrl === 'string' ? i.hostedInvoiceUrl : null,
    amountRemaining: i.amountRemaining,
    status: typeof i.status === 'string' ? i.status : 'open',
  };
};

const LicenseChangePlanModal: React.FC<LicenseChangePlanModalProps> = ({
  isOpen,
  onClose,
  subscriptionId,
  currentProductName,
  newProduct,
  emailExtraCount,
  agentExtraCount,
  hasPendingScheduledChange,
  pendingPlanName,
  onSuccess,
  onSubmittingChange,
  onPendingPayment,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [state, setState] = useState<ModalState>('preview_loading');
  const [preview, setPreview] = useState<SubscriptionChangePlanPreviewView | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Build the shared request body for preview + change-plan. Freshdesk add-on
  // counts are included ONLY when they are concrete numbers; `undefined` means
  // "keep current" (we omit the field entirely).
  const buildRequestBody = () => {
    const body: { productId: string; emailExtraCount?: number; agentExtraCount?: number } = {
      productId: newProduct.id,
    };
    if (typeof emailExtraCount === 'number') body.emailExtraCount = emailExtraCount;
    if (typeof agentExtraCount === 'number') body.agentExtraCount = agentExtraCount;
    return body;
  };

  // Fetch preview on open. The backend explicitly does not reserve anything
  // on this call, so we don't need to release anything on close.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setState('preview_loading');
    setPreview(null);
    setPreviewError(null);

    (async () => {
      try {
        const result = await SubscriptionService.postAppSubscriptionChangePlanPreview(
          subscriptionId,
          buildRequestBody(),
        );
        if (cancelled) return;
        setPreview(result);
        setState('preview_ready');
      } catch (error) {
        if (cancelled) return;
        let message = t('subscription_change_preview_error');
        if (error instanceof ApiError) {
          const parsed = helper.parseApiErrorBody(error);
          if (parsed && parsed.error) {
            message = parsed.error;
          }
        }
        setPreviewError(message);
        setState('preview_error');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subscriptionId, newProduct.id, emailExtraCount, agentExtraCount, t]);

  const direction: PreviewDirection | null = preview?.direction ?? null;
  const currencyDisplay = (preview?.currency ?? 'eur').toUpperCase();

  const titleKey = (() => {
    if (!direction) return 'subscription_change_upgrade_title';
    if (direction === 'upgrade') return 'subscription_change_upgrade_title';
    if (direction === 'sidegrade') return 'subscription_change_sidegrade_title';
    return 'subscription_change_downgrade_title';
  })();

  const confirmKey = (() => {
    if (!direction) return 'subscription_change_upgrade_confirm';
    if (direction === 'upgrade') return 'subscription_change_upgrade_confirm';
    if (direction === 'sidegrade') return 'subscription_change_sidegrade_confirm';
    return 'subscription_change_downgrade_confirm';
  })();

  const handleConfirm = async () => {
    setState('submitting');
    onSubmittingChange?.(true);
    try {
      const raw = await SubscriptionService.postAppSubscriptionChangePlan(
        subscriptionId,
        buildRequestBody(),
      );
      const response = raw as ChangePlanResponse;

      if (response.direction === 'downgrade') {
        const downgrade = response as ChangePlanDowngradeResponse;
        const alreadyScheduled = typeof downgrade.message === 'string' &&
          downgrade.message.toLowerCase().includes('already');
        onSuccess({
          serverDirection: 'downgrade',
          effectiveAt: downgrade.effectiveAt,
          alreadyScheduled,
          newProduct,
        });
      } else {
        const upgrade = response as ChangePlanUpgradeResponse;
        onSuccess({
          serverDirection: upgrade.direction,
          status: upgrade.status ?? null,
          hostedInvoiceUrl: upgrade.hostedInvoiceUrl ?? null,
          amountPaid: upgrade.amountPaid,
          newProduct,
        });
      }
      onClose();
    } catch (error) {
      console.error('Failed to change subscription plan:', error);

      if (error instanceof ApiError) {
        const parsedBody = helper.parseApiErrorBody(error);

        // 409 + errorCode=pending_payment - a previous failed 3DS left an
        // open Stripe invoice. Hand off to the parent's pending-payment
        // modal and close ourselves; the change-plan was NOT applied.
        if (error.status === 409) {
          const invoice = extractPendingInvoice(parsedBody);
          if (invoice) {
            onPendingPayment(invoice);
            onClose();
            return;
          }
        }

        // 503 - backend couldn't verify Stripe state. Treat as transient,
        // surface a soft toast, leave the modal in preview_ready so the
        // user can hit Confirm again as their retry button.
        if (error.status === 503) {
          dispatch(addNotification({
            type: 'warning',
            title: t('notification_error'),
            message: t('subscription_change_transient_error'),
            autoHideDuration: 6000,
          }));
          setState('preview_ready');
          onSubmittingChange?.(false);
          return;
        }

        const message = parsedBody?.error || t('subscription_change_toast_failed');
        dispatch(addNotification({
          type: 'error',
          title: t('notification_error'),
          message,
          autoHideDuration: 8000,
        }));
        setState('preview_ready');
        onSubmittingChange?.(false);
        return;
      }

      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: t('subscription_change_toast_failed'),
        autoHideDuration: 8000,
      }));
      // Drop back to the preview state so the user can retry without
      // reopening the modal - preview data is still valid.
      setState('preview_ready');
      onSubmittingChange?.(false);
    }
  };

  const renderDirectionText = () => {
    if (!preview || !direction) return null;
    if (direction === 'upgrade') {
      return (
        <div className="modal-success-text">
          {t('subscription_change_upgrade_text', { newPlan: newProduct.name })}
        </div>
      );
    }
    if (direction === 'sidegrade') {
      return (
        <div className="modal-success-text">
          {t('subscription_change_sidegrade_text', { newPlan: newProduct.name })}
        </div>
      );
    }
    const effectiveLabel = formatUnix(preview.effectiveAt);
    return (
      <>
        <div className="modal-success-text">
          {t('subscription_change_downgrade_text_part1', {
            currentPlan: currentProductName,
            date: effectiveLabel,
          })}
        </div>
        <div className="modal-success-text">
          {t('subscription_change_downgrade_text_part2', {
            newPlan: newProduct.name,
            date: effectiveLabel,
          })}
        </div>
        <div className="modal-success-text">
          {t('subscription_change_downgrade_text_part3')}
        </div>
      </>
    );
  };

  /**
   * Preview details block - shown alongside the direction-specific text.
   * Surfaces the exact numbers from `/change-plan/preview` so the user sees
   * what they're about to be charged BEFORE confirming.
   */
  const renderPreviewDetails = () => {
    if (!preview || !direction) return null;
    const formatCents = (cents: number) =>
      helper.formatPrice({ amount: cents, currency: currencyDisplay });

    return (
      <div className="modal-details">
        {direction === 'upgrade' && (
          <div className="modal-details-item">
            <span className="modal-details-label">
              {t('subscription_change_preview_amount_due_now_label')}
            </span>
            <span className="modal-details-value">
              {preview.amountDueNowIsApproximate
                ? `≈ ${formatCents(preview.amountDueNow)}`
                : formatCents(preview.amountDueNow)}
            </span>
          </div>
        )}
        {direction !== 'sidegrade' && (
          <div className="modal-details-item">
            <span className="modal-details-label">
              {t('subscription_change_preview_recurring_label')}
            </span>
            <span className="modal-details-value">
              {`${formatCents(preview.oldRecurringPrice)} → ${formatCents(preview.newRecurringPrice)}`}
            </span>
          </div>
        )}
        {direction === 'downgrade' && (
          <div className="modal-details-item">
            <span className="modal-details-label">
              {t('subscription_change_preview_effective_label')}
            </span>
            <span className="modal-details-value">
              {formatUnix(preview.effectiveAt)}
            </span>
          </div>
        )}
      </div>
    );
  };

  const isConfirmDisabled = state !== 'preview_ready';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t(titleKey)}
      hideCloseButton={true}
      isCentered={true}
    >
      {state === 'preview_loading' && (
        <div className="modal-loading">
          <div className="modal-loading-spinner" />
          <div className="modal-loading-text">
            {t('subscription_change_preview_loading')}
          </div>
        </div>
      )}

      {state === 'preview_error' && (
        <div className="modal-success">
          <div className="modal-success-text">
            {previewError || t('subscription_change_preview_error')}
          </div>
          <div className="modal-buttons">
            <button
              className="modal-button confirm"
              onClick={onClose}
            >
              {t('subscription_change_back')}
            </button>
          </div>
        </div>
      )}

      {(state === 'preview_ready' || state === 'submitting') && preview && (
        <div className="modal-success">
          {renderDirectionText()}
          {renderPreviewDetails()}
          {hasPendingScheduledChange && (
            <div className="modal-success-text">
              {t('subscription_change_pending_warning', {
                plannedPlan: pendingPlanName || '',
              })}
            </div>
          )}
          <div className="modal-buttons">
            {/* Button colors depend on direction: for downgrade the dismiss
                (left) is the primary/blue one and the confirm (right) is gray;
                for upgrade/sidegrade it's the opposite (confirm/right is blue). */}
            <button
              className={`modal-button ${direction === 'downgrade' ? 'confirm' : 'cancel'}`}
              onClick={onClose}
              disabled={state === 'submitting'}
            >
              {t('subscription_change_back')}
            </button>
            <button
              className={`modal-button ${direction === 'downgrade' ? 'cancel' : 'confirm'}`}
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
            >
              {state === 'submitting'
                ? t('subscription_change_processing')
                : t(confirmKey)}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default LicenseChangePlanModal;
